import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { AgendaApi, ServiceApi, StepApi } from 'services';
import { toastMessages } from 'config/constants';
import placeholder from '_assets/common/placeholer.png';
import toast from 'react-hot-toast';
import useGenericContext from 'hooks/useGenericContext';
import useLocalStorage from 'hooks/useLocalStorage';

const WeekAgendaContext = createContext(undefined);
const WeekAgendaFunctionsContext = createContext(undefined);

const initialAppointmentState = {
  appointments: [],
  createdAppointments: [],
  initialAppointments: [],
  removedAppointments: [],
  updatedAppointments: [],
};

function WeekAgendaProvider({ children, company, monitor, onLoaded }) {
  const [appointmentState, setAppointmentState] = useLocalStorage('appointments', initialAppointmentState);
  const [activities, setActivities] = useState([]);
  const [currentAppointments, setCurrentAppointments] = useState(null);

  const [agendaLoading, setAgendaLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  useEffect(() => {
    onLoaded?.(agendaLoading);
  }, [agendaLoading, onLoaded]);

  // Load employee appointments
  const getEmployeeAgenda = useCallback(
    async (id) => {
      setAgendaLoading(true);
      try {
        const employeeAgenda = await AgendaApi.getWeekAgendaByEmployee(id);

        let days = [];
        days['Monday'] = 'MO';
        days['Tuesday'] = 'TU';
        days['Wednesday'] = 'WE';
        days['Thursday'] = 'TH';
        days['Friday'] = 'FR';
        days['Saturday'] = 'SA';
        days['Sunday'] = 'SU';

        let daysIndex = [];
        daysIndex['MO'] = 1;
        daysIndex['TU'] = 2;
        daysIndex['WE'] = 3;
        daysIndex['TH'] = 4;
        daysIndex['FR'] = 5;
        daysIndex['SA'] = 6;
        daysIndex['SU'] = 0;

        let existingAppointments = [];
        for (let i = 0; i < employeeAgenda.agenda.activities.length; i++) {
          const employeeAgendaEntry = employeeAgenda.agenda.activities[i];
          const weekDayCode = days[employeeAgendaEntry.week_day];
          const weekDayIndex = daysIndex[weekDayCode];

          const appointment = {
            id: String(employeeAgendaEntry.id),
            title: employeeAgendaEntry.service_name,
            service_id: employeeAgendaEntry.service_id,
            startTime: employeeAgendaEntry.start_time,
            endTime: employeeAgendaEntry.end_time,
            daysOfWeek: [weekDayIndex],
            is_blocked: employeeAgendaEntry.service_status === 'blocked',
          };
          existingAppointments.push(appointment);
        }
        const appointments = {
          ...initialAppointmentState,
          appointments: existingAppointments,
          initialAppointmentState: existingAppointments,
        };
        setAppointmentState(appointments);
      } catch (error) {}
      setAgendaLoading(false);
    },
    [setAppointmentState],
  );

  // Load employee activities
  const getActivities = useCallback(async (companyId, monitorId) => {
    setActivitiesLoading(true);
    try {
      const { data } = await ServiceApi.getCompanyServicesByEmployee(companyId, monitorId);
      const activities = data.map((service) => ({
        productName: service.name,
        text: service.name,
        identifier: service.identifier,
        service_id: service.id,
        img: service.imageURL ? service.imageURL : placeholder,
        desc: service.description,
        level: service.service_level,
        courseType: service.clients_number > 1 ? 'Collectif' : 'Individuel',
        duration: service.duration,
        monitors: service.employees,
        is_blocked: service.is_blocked,
        lowPrice: service.low_season_price,
        peakPrice: service.peak_season_price,
      }));
      setActivities(activities);
    } catch (_) {}
    setActivitiesLoading(false);
  }, []);

  // Create new appointments for the current monitor
  const createAppointments = useCallback(async (monitor, appointments) => {
    let agendaActivities = [];
    for (const appointment of appointments) {
      agendaActivities.push({
        service_id: appointment.service_id,
        start_time: appointment.startTime,
        week_day: appointment.daysOfWeek[0],
      });
    }

    try {
      await AgendaApi.createAgendaByEmployee(monitor?.id, { agenda: { activities: agendaActivities } });
    } catch (e) {
      throw e;
    }
  }, []);

  // Update current monitor's appointments
  const updateAppointments = useCallback(async (appointments) => {
    for (const appointment of appointments) {
      try {
        await AgendaApi.updateWeekAgenda(appointment.id, {
          close_time: appointment.endTime,
          day_number: appointment.daysOfWeek[0],
          open_time: appointment.startTime,
          service_id: appointment.service_id,
        });
      } catch (e) {
        throw e;
      }
    }
  }, []);

  // Remove current monitor's appointments
  const removeAppointments = useCallback(async (appointments) => {
    for (const appointment of appointments) {
      try {
        await AgendaApi.removeWeekAgenda(appointment.id);
      } catch (e) {
        const message =
          `Erreur lors de la suppression du créneau ${appointment.title} ` +
          `(${appointment.startTime} - ${appointment.endTime}) : vérifiez que vous n'avez pas de réservations en cours`;
        toast.error(message);
        throw e;
      }
    }
  }, []);

  // Save all appointment updates
  const saveAppointments = useCallback(
    async (appointmentState) => {
      try {
        if (appointmentState) {
          const { createdAppointments, removedAppointments, updatedAppointments, monitor: m } = appointmentState;

          // if appointments have been created
          if (createdAppointments.length !== 0) await createAppointments(m ?? monitor, createdAppointments);

          // if appointments have been updated
          if (updatedAppointments.length !== 0) await updateAppointments(updatedAppointments);

          // if appointments have been removed
          if (removedAppointments.length !== 0) await removeAppointments(removedAppointments);

          return true;
        }

        return false;
      } catch (e) {
        return false;
      }
    },
    [createAppointments, removeAppointments, updateAppointments, monitor],
  );

  // Save the chosen period
  const savePeriod = useCallback(
    async (values) => {
      try {
        await AgendaApi.createAgendaConfigByEmployee(monitor?.id, values);
        await StepApi.updateStep(company?.id, 'agenda_created');
        toast.success(toastMessages.success.CREATE_AGENDA);
        return true;
      } catch (_) {
        toast.error(toastMessages.error.CREATE_AGENDA);
        return false;
      }
    },
    [company?.id, monitor?.id],
  );

  // Load data that the page needs when monitor or company change
  useEffect(() => {
    if (monitor?.id && company?.id) {
      setAppointmentState(initialAppointmentState);
      setTimeout(() => getEmployeeAgenda(monitor.id));
      setTimeout(() => getActivities(company.id, monitor.id));
    }
  }, [company?.id, getActivities, getEmployeeAgenda, monitor?.id, setAppointmentState]);

  const value = useMemo(
    () => ({
      appointmentState,
      activities,
      agendaLoading,
      activitiesLoading,
      currentAppointments,
    }),
    [appointmentState, activities, agendaLoading, activitiesLoading, currentAppointments],
  );

  const functions = useMemo(
    () => ({
      saveAppointments,
      savePeriod,
      setAppointmentState,
      setCurrentAppointments,
    }),
    [saveAppointments, savePeriod, setAppointmentState, setCurrentAppointments],
  );

  return (
    <WeekAgendaContext.Provider value={value}>
      <WeekAgendaFunctionsContext.Provider value={functions}>{children}</WeekAgendaFunctionsContext.Provider>
    </WeekAgendaContext.Provider>
  );
}

function useWeekAgenda() {
  return useGenericContext(
    WeekAgendaContext,
    'useWeekAgenda cannot be used outside of a WeekAgendaProvider component.',
  );
}

function useWeekAgendaFunctions() {
  return useGenericContext(
    WeekAgendaFunctionsContext,
    'useWeekAgendaFunctions cannot be used outside of a WeekAgendaProvider component',
  );
}

export { WeekAgendaProvider, useWeekAgenda, useWeekAgendaFunctions };
